// Strukturierter Fachrichtungs-Katalog für Praxen + Kontakte.
//
// Reihenfolge: Allgemeinmedizinischer Block alphabetisch nach Label, danach
// Zahnmedizin als eigener Block (ebenfalls alphabetisch), zum Schluss
// "Sonstiges" als Catch-all-Eintrag. Sortierung mit deutscher Locale, damit
// Umlaute (ä, ö, ü) korrekt einsortiert werden.

const labelCmp = (a, b) => a.label.localeCompare(b.label, 'de');

const FACHRICHTUNG_ALLGEMEIN = [
  { value: 'allgemeinmedizin', label: 'Allgemeinmedizin' },
  { value: 'anaesthesiologie', label: 'Anästhesiologie' },
  { value: 'angiologie', label: 'Angiologie' },
  { value: 'arbeitsmedizin', label: 'Arbeitsmedizin' },
  { value: 'augenheilkunde', label: 'Augenheilkunde' },
  { value: 'chirurgie_allgemein', label: 'Chirurgie (allgemein)' },
  { value: 'dermatologie', label: 'Dermatologie' },
  { value: 'endokrinologie_diabetologie', label: 'Endokrinologie und Diabetologie' },
  { value: 'gastroenterologie', label: 'Gastroenterologie' },
  { value: 'chirurgie_gefaess', label: 'Gefäßchirurgie' },
  { value: 'gynaekologie', label: 'Gynäkologie' },
  { value: 'haematologie_onkologie', label: 'Hämatologie und Onkologie' },
  { value: 'chirurgie_herz', label: 'Herzchirurgie' },
  { value: 'hno', label: 'HNO (Hals-Nasen-Ohren)' },
  { value: 'humangenetik', label: 'Humangenetik' },
  { value: 'hygiene_umwelt', label: 'Hygiene und Umweltmedizin' },
  { value: 'infektiologie', label: 'Infektiologie' },
  { value: 'innere_medizin', label: 'Innere Medizin' },
  { value: 'kardiologie', label: 'Kardiologie' },
  { value: 'kinderjugendmedizin', label: 'Kinder- und Jugendmedizin' },
  { value: 'kinderjugendpsychiatrie', label: 'Kinder- und Jugendpsychiatrie' },
  { value: 'chirurgie_kinder', label: 'Kinderchirurgie' },
  { value: 'labormedizin', label: 'Labormedizin' },
  { value: 'mikrobiologie', label: 'Mikrobiologie' },
  { value: 'mkg_chirurgie', label: 'Mund-Kiefer-Gesichtschirurgie' },
  { value: 'nephrologie', label: 'Nephrologie' },
  { value: 'neurochirurgie', label: 'Neurochirurgie' },
  { value: 'neurologie', label: 'Neurologie' },
  { value: 'nuklearmedizin', label: 'Nuklearmedizin' },
  { value: 'chirurgie_ortho_unfall', label: 'Orthopädie und Unfallchirurgie' },
  { value: 'pathologie', label: 'Pathologie' },
  { value: 'pharmakologie', label: 'Pharmakologie' },
  { value: 'physikalische_reha', label: 'Physikalische und Rehabilitative Medizin' },
  { value: 'chirurgie_plastisch', label: 'Plastische Chirurgie' },
  { value: 'pneumologie', label: 'Pneumologie' },
  { value: 'psychiatrie_psychotherapie', label: 'Psychiatrie und Psychotherapie' },
  { value: 'psychosomatik', label: 'Psychosomatische Medizin' },
  { value: 'radiologie', label: 'Radiologie' },
  { value: 'rechtsmedizin', label: 'Rechtsmedizin' },
  { value: 'rheumatologie', label: 'Rheumatologie' },
  { value: 'sportmedizin', label: 'Sportmedizin' },
  { value: 'strahlentherapie', label: 'Strahlentherapie' },
  { value: 'chirurgie_thorax', label: 'Thoraxchirurgie' },
  { value: 'transfusionsmedizin', label: 'Transfusionsmedizin' },
  { value: 'urologie', label: 'Urologie' },
  { value: 'chirurgie_viszeral', label: 'Viszeralchirurgie' },
].sort(labelCmp);

const FACHRICHTUNG_ZAHN = [
  { value: 'endodontie', label: 'Endodontie' },
  { value: 'kieferorthopaedie', label: 'Kieferorthopädie' },
  { value: 'kinderzahnheilkunde', label: 'Kinderzahnheilkunde' },
  { value: 'oralchirurgie', label: 'Oralchirurgie' },
  { value: 'parodontologie', label: 'Parodontologie' },
  { value: 'prothetik', label: 'Prothetik' },
  { value: 'zahnarzt_allgemein', label: 'Zahnarzt (allgemein)' },
].sort(labelCmp);

export const FACHRICHTUNG_OPTIONS = [
  ...FACHRICHTUNG_ALLGEMEIN,
  ...FACHRICHTUNG_ZAHN,
  { value: 'sonstiges', label: 'Sonstiges' },
];

export const FACHRICHTUNG_LABEL = Object.fromEntries(
  FACHRICHTUNG_OPTIONS.map((o) => [o.value, o.label])
);

export const FACHRICHTUNG_SLUGS = new Set(FACHRICHTUNG_OPTIONS.map((o) => o.value));

/**
 * Ermittelt den anzuzeigenden Fachgebiets-Text einer Praxis.
 *
 * Priorität: strukturiertes `fachgebiet_neu` (als lesbares Label, sonst der
 * rohe Slug), Fallback auf das alte Freitext-Feld `fachgebiet`. Gibt `null`
 * zurück, wenn beides leer ist — Aufrufer können so z. B. auf `'—'` mappen.
 */
export function fachgebietAnzeige(praxis) {
  if (praxis?.fachgebiet_neu) {
    return FACHRICHTUNG_LABEL[praxis.fachgebiet_neu] ?? praxis.fachgebiet_neu;
  }
  return praxis?.fachgebiet || null;
}

// Lookup für CSV-Import: jeder bekannte Slug UND jedes Label (kleingeschrieben,
// getrimmt) mappt zurück auf den Slug. Alles andere → null. So kann der
// Importer "Kardiologie" aus einer Quelle ebenso erkennen wie "kardiologie".
const _IMPORT_LOOKUP = (() => {
  const map = new Map();
  for (const o of FACHRICHTUNG_OPTIONS) {
    map.set(o.value.toLowerCase(), o.value);
    map.set(o.label.toLowerCase(), o.value);
  }
  return map;
})();

/**
 * Wandelt einen CSV-Wert in einen gültigen Fachrichtungs-Slug oder gibt einen
 * leeren String zurück, wenn keine eindeutige Zuordnung möglich ist.
 */
export function normalisiereFachrichtungImport(raw) {
  if (raw == null) return '';
  const norm = String(raw).trim().toLowerCase();
  if (!norm) return '';
  return _IMPORT_LOOKUP.get(norm) ?? '';
}
